import { createSlice } from "@reduxjs/toolkit"
import { createAppAsyncThunk } from "app/hooks"
import profileAPI from "./profileAPI"
import { DEFAULT_BODY_DISPLAY_UNIT, resetStoreAction } from "config"
import {
  IBodyDisplayUnits,
  IChangeBodyAndGoalPayload,
  IDailyCaloriesMode,
  IManualUpdateGoalPayload,
  IProfileState,
  IUpdateBodyAndUnitPayload,
} from "./profileTypes"
import { is404Response } from "utils"
import onboardingAPI from "features/onboarding/onboardingAPI"
import convertResponseToClientGoal from "utils/convertResponseToClientGoal"
import { setOnboardingGoal } from "features/onboarding/onboardingSlice"
import { IResponseGoal } from "features/onboarding/types"

export const getBody = createAppAsyncThunk(
  "profile/getBody",
  async (_, { rejectWithValue }) => {
    try {
      const { body } = await profileAPI.getBody()
      return body
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getLatestUserScan = createAppAsyncThunk(
  "profile/getLatestUserScan",
  async (_, { rejectWithValue }) => {
    try {
      const { userMeasurements } = await profileAPI.getLatestUserScan()
      const latestUserScan = userMeasurements?.[0]

      let lastMeasurementWeight: number | undefined
      if (latestUserScan?.measurementId) {
        const measurement = await profileAPI.getMeasurementById(
          latestUserScan.measurementId,
        )
        lastMeasurementWeight = measurement.body?.weight
      }

      return { latestUserScan, lastMeasurementWeight }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const updateBodyAndUnits = createAppAsyncThunk(
  "profile/updateBodyAndUnits",
  async (
    { body, bodyDisplayUnits }: IUpdateBodyAndUnitPayload,
    { rejectWithValue },
  ) => {
    const updateBodyPromise = body
      ? profileAPI.updateBody(body)
      : Promise.resolve()

    const updateBodyDisplayUnitsPromise = bodyDisplayUnits
      ? profileAPI.updateBodyDisplayUnits(bodyDisplayUnits)
      : Promise.resolve()

    try {
      await Promise.all([updateBodyPromise, updateBodyDisplayUnitsPromise])
      return { body, bodyDisplayUnits }
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getBodyDisplayUnits = createAppAsyncThunk(
  "profile/getBodyDisplayUnits",
  async (_, { rejectWithValue, dispatch }) => {
    try {
      const { displayUnits } = await profileAPI.getBodyDisplayUnits()
      return displayUnits
    } catch (err: any) {
      if (is404Response(err)) {
        // Silently create the default setting on the server
        dispatch(updateBodyDisplayUnits(DEFAULT_BODY_DISPLAY_UNIT))
        return DEFAULT_BODY_DISPLAY_UNIT
      }
      return rejectWithValue(err)
    }
  },
)

export const updateBodyDisplayUnits = createAppAsyncThunk(
  "profile/updateBodyDisplayUnits",
  async (bodyDisplayUnits: IBodyDisplayUnits, { rejectWithValue }) => {
    try {
      await profileAPI.updateBodyDisplayUnits(bodyDisplayUnits)
      return bodyDisplayUnits
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const getProfile = createAppAsyncThunk(
  "profile/getProfile",
  async (_, { rejectWithValue }) => {
    try {
      const response = await profileAPI.getProfile()
      return response
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const changeGoal = createAppAsyncThunk(
  "profile/changeGoal",
  async (payload: IChangeBodyAndGoalPayload, { rejectWithValue, dispatch }) => {
    try {
      if (payload.bodyDisplayUnits) {
        await dispatch(updateBodyDisplayUnits(payload.bodyDisplayUnits))
      }

      const response = await onboardingAPI.setGoal(payload.changeGoalPayload)
      const updatedGoal = convertResponseToClientGoal(response)

      dispatch(setOnboardingGoal(updatedGoal))
      dispatch(getBody())

      return updatedGoal
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

export const manualUpdateGoal = createAppAsyncThunk(
  "profile/manualUpdateGoal",
  async (payload: IManualUpdateGoalPayload, { rejectWithValue, dispatch }) => {
    try {
      const response = (await profileAPI.manualUpdateGoal(
        payload,
      )) as IResponseGoal
      const updatedGoal = convertResponseToClientGoal(response)

      dispatch(setOnboardingGoal(updatedGoal))
      dispatch(getBody())

      return updatedGoal
    } catch (err: any) {
      return rejectWithValue(err)
    }
  },
)

const initialState: IProfileState = {
  changeGoalLoading: false,
  changeGoalFailed: undefined,
  changeGoalSuccess: undefined,

  profile: undefined,
  getProfileLoading: false,
  getProfileFailed: undefined,

  body: {
    weight: 0,
    height: 0,
  },
  getBodyLoading: false,
  getBodyFailed: undefined,

  updateBodyAndUnitsLoading: false,
  updateBodyAndUnitsFailed: undefined,
  updateBodyAndUnitsSuccess: undefined,

  bodyDisplayUnits: DEFAULT_BODY_DISPLAY_UNIT,
  getBodyDisplayUnitsLoading: false,
  getBodyDisplayUnitsFailed: undefined,

  updateBodyDisplayUnitsLoading: false,
  updateBodyDisplayUnitsSuccess: undefined,
  updateBodyDisplayUnitsFailed: undefined,

  manualUpdateGoalLoading: false,
  manualUpdateGoalFailed: undefined,
  manualUpdateGoalSuccess: undefined,

  latestUserScan: undefined,
  lastMeasurementWeight: undefined,
  getLatestUserScanLoading: false,
  getLatestUserScanFailed: undefined,

  userPreferences: {
    dailyCaloriesMode: IDailyCaloriesMode.REMAINING,
  },
  refreshCount: 0,
}

export const profileSlice = createSlice({
  name: "profile",
  initialState,
  reducers: {
    setDailyCaloriesMode: (state, { payload }) => {
      state.userPreferences.dailyCaloriesMode = payload
    },
    incrementRefreshCount: (state) => {
      state.refreshCount += 1
    },
  },

  extraReducers(builder) {
    builder
      .addCase(changeGoal.pending, (state, { meta: { arg } }) => {
        const { weight, height } = arg.changeGoalPayload
        state.body = {
          weight,
          height,
        }
        state.changeGoalLoading = true
        state.changeGoalFailed = undefined
        state.changeGoalSuccess = undefined
      })
      .addCase(changeGoal.fulfilled, (state, { payload }) => {
        state.changeGoalLoading = false
        state.changeGoalSuccess = payload
      })
      .addCase(changeGoal.rejected, (state, { payload }) => {
        state.changeGoalLoading = false
        state.changeGoalFailed = payload
      })

      .addCase(getProfile.pending, (state) => {
        state.getProfileLoading = true
        state.getProfileFailed = undefined
      })
      .addCase(getProfile.fulfilled, (state, { payload }) => {
        state.getProfileLoading = false
        state.profile = payload
      })
      .addCase(getProfile.rejected, (state, { payload }) => {
        state.getProfileLoading = false
        state.getProfileFailed = payload
      })

      .addCase(getBody.pending, (state) => {
        state.getBodyLoading = true
        state.getBodyFailed = undefined
      })
      .addCase(getBody.fulfilled, (state, { payload }) => {
        state.getBodyLoading = false
        state.body = payload
      })
      .addCase(getBody.rejected, (state, { payload }) => {
        state.getBodyLoading = false

        if (is404Response(payload)) {
          state.body = { weight: 0, height: 0 }
        } else {
          state.getBodyFailed = payload
        }
      })

      .addCase(updateBodyAndUnits.pending, (state) => {
        state.updateBodyAndUnitsLoading = true
        state.updateBodyAndUnitsFailed = undefined
        state.updateBodyAndUnitsSuccess = undefined
      })
      .addCase(updateBodyAndUnits.fulfilled, (state, { payload }) => {
        state.updateBodyAndUnitsLoading = false
        state.updateBodyAndUnitsSuccess = payload
        if (payload.body) {
          state.body = payload.body
        }

        if (payload.bodyDisplayUnits) {
          state.bodyDisplayUnits = payload.bodyDisplayUnits
        }
      })
      .addCase(updateBodyAndUnits.rejected, (state, { payload }) => {
        state.updateBodyAndUnitsLoading = false
        state.updateBodyAndUnitsFailed = payload
      })

      .addCase(getBodyDisplayUnits.pending, (state) => {
        state.getBodyDisplayUnitsLoading = true
        state.getBodyDisplayUnitsFailed = undefined
      })
      .addCase(getBodyDisplayUnits.fulfilled, (state, { payload }) => {
        state.getBodyDisplayUnitsLoading = false
        state.bodyDisplayUnits = payload
      })
      .addCase(getBodyDisplayUnits.rejected, (state, { payload }) => {
        state.getBodyDisplayUnitsLoading = false

        if (is404Response(payload)) {
          state.bodyDisplayUnits = DEFAULT_BODY_DISPLAY_UNIT
        } else {
          state.getBodyDisplayUnitsFailed = payload
        }
      })

      .addCase(updateBodyDisplayUnits.pending, (state) => {
        state.updateBodyDisplayUnitsLoading = true
        state.updateBodyDisplayUnitsFailed = undefined
        state.updateBodyDisplayUnitsSuccess = undefined
      })
      .addCase(updateBodyDisplayUnits.fulfilled, (state, { payload }) => {
        state.updateBodyDisplayUnitsLoading = false
        state.updateBodyDisplayUnitsSuccess = payload
        state.bodyDisplayUnits = payload
      })
      .addCase(updateBodyDisplayUnits.rejected, (state, { payload }) => {
        state.updateBodyDisplayUnitsLoading = false
        state.updateBodyDisplayUnitsFailed = payload
      })

      .addCase(manualUpdateGoal.pending, (state) => {
        state.manualUpdateGoalLoading = true
        state.manualUpdateGoalFailed = undefined
        state.manualUpdateGoalSuccess = undefined
      })
      .addCase(manualUpdateGoal.fulfilled, (state, { payload }) => {
        state.manualUpdateGoalLoading = false
        state.manualUpdateGoalSuccess = payload
      })
      .addCase(manualUpdateGoal.rejected, (state, { payload }) => {
        state.manualUpdateGoalLoading = false
        state.manualUpdateGoalFailed = payload
      })

      .addCase(getLatestUserScan.pending, (state) => {
        state.getLatestUserScanLoading = true
        state.getLatestUserScanFailed = undefined
      })
      .addCase(getLatestUserScan.fulfilled, (state, { payload }) => {
        state.getLatestUserScanLoading = false
        state.latestUserScan = payload.latestUserScan
        state.lastMeasurementWeight = payload.lastMeasurementWeight
      })
      .addCase(getLatestUserScan.rejected, (state, { payload }) => {
        state.getLatestUserScanLoading = false
        state.getLatestUserScanFailed = payload
      })

      .addCase(resetStoreAction, () => {
        return initialState
      })
  },
})

export const { setDailyCaloriesMode, incrementRefreshCount } = profileSlice.actions

export default profileSlice.reducer
